import React, { useCallback, useState } from "react";
import cx from "classnames";
import styles from "./DeleteBox.module.scss";
import { Contract, ContractStatus } from "../../data/models/ContractTypes";
import { T } from "../translation/T";
import { Button } from "../interactions/Buttons/Button";
import { Status } from "../../data/types";
import { PersistentOverlay } from "../overlay/PersistentOverlay";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dynamic } from "../animate/Dynamic";
import { GenericError } from "../boxes/GenericError";
import { dataContracts } from "../../data/dataContracts";
import { Progress } from "../progress/Progress";
import { contractSidebarState } from "../../state/uiState";
import { useSetAtom } from "jotai";

interface Props {
  contract: Contract;
}

function getStatus(contract: Contract): Status {
  if (
    contract.status === ContractStatus.SALES_INPUT ||
    contract.status === ContractStatus.MERCHANT_INPUT ||
    contract.status === ContractStatus.MERCHANT_SIGNING
  ) {
    return Status.DEFAULT;
  }

  return Status.DISABLED;
}

export const DeleteBox: React.FunctionComponent<Props> = ({ contract }) => {
  const [open, setOpen] = useState(false);
  const status = getStatus(contract);
  const queryClient = useQueryClient();
  const toggleMenu = useSetAtom(contractSidebarState);

  const {
    mutate: onDelete,
    isPending,
    isError,
    isSuccess,
  } = useMutation({
    mutationFn: () => dataContracts.deleteContract(contract.contractId),
  });

  const onNav = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: dataContracts.getContractKey(contract.contractId),
        refetchType: "all",
      }),
      queryClient.invalidateQueries({
        queryKey: dataContracts.getContractsKey(),
        refetchType: "all",
      }),
    ]);
    toggleMenu(false);
  }, [contract, toggleMenu, queryClient]);

  if (contract.status === ContractStatus.CANCELLED) {
    return (
      <div className={cx(styles.deleteBox, styles.box, "text-mini")}>
        <T>This contract has been deleted.</T>
      </div>
    );
  }

  if (status === Status.DISABLED) {
    return (
      <div className={cx(styles.deleteBox, styles.box, "text-mini")}>
        <T>Due to the state of this contract, it can not be deleted.</T>
      </div>
    );
  }

  return (
    <div className={styles.deleteBox}>
      <PersistentOverlay
        className={styles.overlay}
        status={Status.ERROR}
        open={open}
        onClose={() => setOpen(false)}
      >
        <div className={cx(styles.overlay)}>
          <h4>
            <T>Are you sure?</T>
          </h4>
          <T>Delete contract for </T>
          <b>{contract.contractData.companyName}</b> (
          {contract.contractData.organizationNumber})
          <Dynamic name={isError ? "error" : isSuccess ? "success" : "default"}>
            {isError ? (
              <GenericError>
                <T>Something went wrong, try again?</T>
              </GenericError>
            ) : null}
            {isSuccess ? (
              <div className={styles.progress}>
                <div>
                  <Progress
                    duration={3000}
                    progress={true}
                    callback={() => onNav()}
                  />
                </div>
              </div>
            ) : null}
          </Dynamic>
          <Button
            block
            status={
              isPending
                ? Status.PENDING
                : isSuccess
                ? Status.DISABLED
                : Status.DEFAULT
            }
            onClick={() => onDelete()}
            className={cx("danger-button", "m-top-20")}
          >
            <T>Yes, delete contract</T>
          </Button>
        </div>
      </PersistentOverlay>
      <Button
        block
        className={cx("danger-button", "small")}
        onClick={() => setOpen(true)}
        status={getStatus(contract)}
      >
        <T>Delete contract</T>
      </Button>
    </div>
  );
};
